import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { BookOpen, Video, Clock, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVELS = ['beginner', 'intermediate', 'advanced', 'professional', 'olympic'];

function CreateCourse() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    sport_category: '',
    title: '',
    description: '',
    level: 'beginner',
    price: '',
    duration_weeks: '',
    includes_live_sessions: false,
    includes_recorded_videos: false,
    max_students: '50',
    image_url: ''
  });
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [sportsCategories, setSportsCategories] = useState([]);
  
  useEffect(() => {
    fetchSportsCategories();
    if (isEdit) {
      fetchCourse();
    }
  }, [id]);
  
  const fetchSportsCategories = async () => {
    try {
      const response = await axios.get(`${API}/sports/categories`);
      setSportsCategories(response.data);
    } catch (error) {
      console.error('Error fetching sports categories:', error);
    }
  };
  
  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}`, { withCredentials: true });
      const course = response.data;
      setFormData({
        sport_category: course.sport_category,
        title: course.title,
        description: course.description,
        level: course.level,
        price: course.price.toString(),
        duration_weeks: course.duration_weeks.toString(),
        includes_live_sessions: course.includes_live_sessions,
        includes_recorded_videos: course.includes_recorded_videos,
        max_students: course.max_students.toString(),
        image_url: course.image_url || ''
      });
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        duration_weeks: parseInt(formData.duration_weeks),
        max_students: parseInt(formData.max_students)
      };
      
      if (isEdit) {
        await axios.put(`${API}/courses/${id}`, payload, { withCredentials: true });
        toast.success('Course updated successfully!');
      } else {
        await axios.post(`${API}/courses`, payload, { withCredentials: true });
        toast.success('Course created successfully!');
      }
      
      navigate('/coach/dashboard');
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} course`);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-3 border-premium-gold/20 border-t-premium-gold"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-deep-dark py-12" data-testid="create-course-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <span className="inline-block px-4 py-1 bg-premium-gold/20 text-premium-gold rounded-full text-sm font-semibold tracking-wider mb-4">
            Course Builder
          </span>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: 'Fraunces, serif' }}
            data-testid="page-title"
          >
            {isEdit ? 'Edit' : 'Create'} <span className="gold-gradient">Course</span>
          </h1>
        </div>
        
        <div className="bg-dark-surface p-8 rounded-xl border border-premium-gold/30/50">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">
                Sport Category <span className="text-premium-gold">*</span>
              </label>
              <select
                name="sport_category"
                value={formData.sport_category}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
                data-testid="sport-select"
              >
                <option value="">Select a sport</option>
                {sportsCategories.map((category) => (
                  <optgroup key={category.category_id} label={`${category.icon} ${category.category_name}`}>
                    {category.sports.map((sport) => (
                      <option key={sport.id} value={sport.id}>
                        {sport.icon} {sport.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">
                Course Title <span className="text-premium-gold">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
                placeholder="e.g., Cricket Batting Masterclass"
                data-testid="title-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">
                Description <span className="text-premium-gold">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
                placeholder="Describe what students will learn..."
                data-testid="description-input"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Level <span className="text-premium-gold">*</span>
                </label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all capitalize"
                  data-testid="level-select"
                >
                  {LEVELS.map((level) => (
                    <option key={level} value={level} className="capitalize">{level}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Price (₹) <span className="text-premium-gold">*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
                  placeholder="5000"
                  data-testid="price-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Duration (weeks) <span className="text-premium-gold">*</span>
                </label>
                <input
                  type="number"
                  name="duration_weeks"
                  value={formData.duration_weeks}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
                  placeholder="8"
                  data-testid="duration-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Max Students
                </label>
                <input
                  type="number"
                  name="max_students"
                  value={formData.max_students}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
                  placeholder="50"
                  data-testid="max-students-input"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-white/80 mb-2">
                Image URL (optional)
              </label>
              <input
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-deep-dark border border-premium-gold/30 rounded-lg text-white placeholder-white/40 focus:border-premium-gold focus:ring-2 focus:ring-premium-gold/30 focus:outline-none transition-all"
                placeholder="https://example.com/image.jpg"
                data-testid="image-url-input"
              />
            </div>
            
            <div className="space-y-3 p-4 bg-deep-dark/50 rounded-lg border border-premium-gold/30/30">
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="includes_live_sessions"
                  checked={formData.includes_live_sessions}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-premium-gold/30 bg-deep-dark text-premium-gold focus:ring-premium-gold"
                  data-testid="live-sessions-checkbox"
                />
                <span className="text-sm font-semibold text-white/80 group-hover:text-white flex items-center gap-2">
                  <Video className="w-4 h-4 text-premium-gold" strokeWidth={1.5} />
                  Includes Live Sessions
                </span>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="includes_recorded_videos"
                  checked={formData.includes_recorded_videos}
                  onChange={handleChange}
                  className="w-5 h-5 rounded border-premium-gold/30 bg-deep-dark text-premium-gold focus:ring-premium-gold"
                  data-testid="recorded-videos-checkbox"
                />
                <span className="text-sm font-semibold text-white/80 group-hover:text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-premium-gold" strokeWidth={1.5} />
                  Includes Recorded Videos
                </span>
              </label>
            </div>
            
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/coach/dashboard')}
                className="flex-1 bg-deep-dark hover:bg-deep-dark text-white font-bold tracking-wider px-8 py-4 rounded-lg transition-all border border-premium-gold/30/50"
                data-testid="cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-premium-gold hover:bg-premium-gold-dark text-white font-bold tracking-wider px-8 py-4 rounded-lg transition-all duration-300 hover:shadow-glow-red disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-button"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {isEdit ? 'Update Course' : 'Create Course'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateCourse;
